import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'cosmicards_whats_new_seen';

/**
 * "What's New" auto-show modal.
 * Polls /api/whats-new for a build_id + slides. If localStorage's last-seen
 * build_id differs from the current one, auto-opens the modal once. Saving
 * the new build_id on close prevents re-prompting until the next deploy bump.
 */
export default function WhatsNewModal() {
  const [build, setBuild] = useState(null);
  const [open, setOpen] = useState(false);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Skip on unauthenticated routes — modal would overlay the login form.
        const path = (typeof window !== 'undefined' && window.location && window.location.pathname) || '';
        const authPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-email'];
        if (authPaths.some(p => path.startsWith(p))) return;

        const res = await axios.get(`${API}/whats-new`);
        if (cancelled) return;
        const { build_id, slides } = res.data || {};
        if (!build_id || !Array.isArray(slides) || slides.length === 0) return;
        setBuild({ build_id, slides });
        const lastSeen = localStorage.getItem(STORAGE_KEY);
        if (lastSeen !== build_id) setOpen(true);
      } catch {
        /* silent — feature is non-critical */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const close = useCallback(() => {
    if (build?.build_id) {
      try { localStorage.setItem(STORAGE_KEY, build.build_id); } catch { /* ignore */ }
    }
    setOpen(false);
  }, [build]);

  // Escape key closes the modal
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (!build || !open) return null;

  const slides = build.slides;
  const slide = slides[idx];
  const isLast = idx === slides.length - 1;
  const isFirst = idx === 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 pointer-events-none"
          data-testid="whats-new-overlay"
        >
          <motion.div
            initial={{ scale: 0.92, opacity: 0, y: 12 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.92, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="relative w-full max-w-md bg-gradient-to-br from-babylon-obsidian to-babylon-void border border-babylon-gold/40 rounded-2xl p-6 shadow-2xl shadow-babylon-gold/10 pointer-events-auto"
            data-testid="whats-new-modal"
          >
            <button
              onClick={close}
              className="absolute top-3 right-3 text-babylon-text-dim hover:text-babylon-gold p-1 rounded-md hover:bg-babylon-gold/10 transition-colors"
              data-testid="whats-new-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="flex items-center gap-2 mb-4 text-[11px] tracking-[0.25em] uppercase text-babylon-gold/70">
              <Sparkles className="w-3.5 h-3.5" />
              <span>What's New</span>
            </div>

            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                initial={{ opacity: 0, x: 30 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -30 }}
                transition={{ duration: 0.2 }}
                className="text-center"
                data-testid={`whats-new-slide-${slide.id}`}
              >
                <div className="text-6xl mb-4 select-none" aria-hidden>{slide.icon}</div>
                <h2 className="font-heading text-2xl text-babylon-gold mb-1">{slide.title}</h2>
                <p className="text-[11px] text-babylon-text-dim/80 tracking-wider uppercase mb-4">{slide.subtitle}</p>
                <p className="text-sm text-babylon-text leading-relaxed mb-5">{slide.body}</p>
                {slide.cta && (
                  <Link
                    to={slide.cta.to}
                    onClick={close}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-babylon-gold text-babylon-void font-semibold text-sm hover:bg-amber-400 transition-colors"
                    data-testid="whats-new-cta"
                  >
                    {slide.cta.label} →
                  </Link>
                )}
              </motion.div>
            </AnimatePresence>

            {/* Pagination dots */}
            <div className="flex justify-center gap-1.5 mt-6">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => setIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? 'w-6 bg-babylon-gold' : 'w-1.5 bg-babylon-gold/30 hover:bg-babylon-gold/50'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                  data-testid={`whats-new-dot-${i}`}
                />
              ))}
            </div>

            <div className="flex items-center justify-between mt-5">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIdx(i => Math.max(0, i - 1))}
                disabled={isFirst}
                className="text-babylon-text-dim hover:text-babylon-gold disabled:opacity-30"
                data-testid="whats-new-prev"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>

              {isLast ? (
                <Button
                  size="sm"
                  onClick={close}
                  className="bg-babylon-gold hover:bg-amber-400 text-babylon-void font-semibold"
                  data-testid="whats-new-finish"
                >
                  Got it ✦
                </Button>
              ) : (
                <Button
                  size="sm"
                  onClick={() => setIdx(i => Math.min(slides.length - 1, i + 1))}
                  className="bg-babylon-gold/20 hover:bg-babylon-gold/30 text-babylon-gold border border-babylon-gold/40"
                  data-testid="whats-new-next"
                >
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
